import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Loader2, LogOut, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const TYPES = [
  { key: "drafts", label: "Drafts" },
  { key: "projects", label: "Projects" },
  { key: "companies", label: "Companies" },
  { key: "regions", label: "Regions" },
];

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const Chip = ({ label, count, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border inline-flex items-center gap-2 ${
      active ? "bg-bb-navy text-white border-bb-navy" : "bg-white text-bb-navy border-border"
    }`}
  >
    {label}
    {typeof count === "number" && (
      <span className={`text-[10px] font-mono px-1.5 py-0.5 ${active ? "bg-white text-bb-navy" : "bg-bb-navy text-white"}`}>
        {count}
      </span>
    )}
  </button>
);

export default function AdminSubmissionsPage() {
  const nav = useNavigate();
  const [type, setType] = useState("drafts");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ drafts: 0, projects: 0, companies: 0, regions: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const loadCounts = useCallback(async () => {
    try {
      const r = await api.get("/admin/submissions", { withCredentials: true });
      setCounts(r.data || {});
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/submissions/${type}`, { withCredentials: true });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error(`Failed to load ${type} submissions`); }
    finally { setLoading(false); }
  }, [type]);

  useEffect(() => { load(); loadCounts(); }, [load, loadCounts]);

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const setRowBusy = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const act = async (id, action) => {
    setRowBusy(id, true);
    try {
      await api.post(`/admin/submissions/${type}/${id}/${action}`, null, { withCredentials: true });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      setItems((xs) => xs.filter((x) => x.id !== id));
      loadCounts();
    } catch {
      toast.error(`${action} failed`);
    } finally { setRowBusy(id, false); }
  };

  const renderBody = (item) => {
    if (type === "drafts") {
      return (
        <>
          {item.excerpt && <p className="text-sm text-bb-slate leading-relaxed">{item.excerpt}</p>}
          <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
            <span>slug: {item.slug}</span>
            {item.category && <span>category: {item.category}</span>}
            {item.commodity && <span>commodity: {item.commodity}</span>}
            {item.region && <span>region: {item.region}</span>}
            {item.company && <span>company: {item.company}</span>}
            {item.project && <span>project: {item.project}</span>}
          </div>
        </>
      );
    }
    if (type === "projects") {
      return (
        <>
          {item.description && <p className="text-sm text-bb-slate leading-relaxed">{item.description}</p>}
          <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
            {item.operator && <span>operator: {item.operator}</span>}
            {item.region && <span>region: {item.region}</span>}
            {item.stage && <span>stage: {item.stage}</span>}
            {item.projectStatus && <span>lifecycle: {item.projectStatus}</span>}
            {item.commodity?.length ? <span>commodity: {item.commodity.join(", ")}</span> : null}
          </div>
        </>
      );
    }
    if (type === "companies") {
      return (
        <>
          {item.description && <p className="text-sm text-bb-slate leading-relaxed">{item.description}</p>}
          <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
            {item.ticker && <span>ticker: {item.ticker}</span>}
            {item.exchange && <span>exchange: {item.exchange}</span>}
            {item.headquarters && <span>hq: {item.headquarters}</span>}
            {item.website && <span>web: {item.website}</span>}
            {item.commodities?.length ? <span>commodities: {item.commodities.join(", ")}</span> : null}
          </div>
        </>
      );
    }
    // regions
    return (
      <>
        {item.summary && <p className="text-sm text-bb-slate leading-relaxed">{item.summary}</p>}
        <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
          {item.keyCommodities?.length ? <span>key commodities: {item.keyCommodities.join(", ")}</span> : null}
          {item.infrastructureNotes && <span>infra: {item.infrastructureNotes}</span>}
        </div>
      </>
    );
  };

  const title = TYPES.find((t) => t.key === type)?.label || type;

  return (
    <div data-testid="admin-submissions-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Submissions</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">
              Agent Review Queue — {title} ({items.length})
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
          >
            <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
        <AdminTabs />
        <div className="bb-container py-2 flex gap-2 overflow-x-auto">
          {TYPES.map((t) => (
            <Chip
              key={t.key}
              label={t.label}
              count={counts[t.key] || 0}
              active={type === t.key}
              onClick={() => setType(t.key)}
              testId={`submissions-tab-${t.key}`}
            />
          ))}
        </div>
      </header>

      <div className="bb-container py-4 space-y-3">
        {loading ? (
          <div className="text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center">
            <Inbox size={20} className="text-bb-copper mx-auto" />
            <div className="bb-overline text-bb-copper mt-3">No pending {title.toLowerCase()}</div>
            <p className="text-sm text-bb-slate mt-2">
              When agents submit content it will land here for review.
            </p>
          </div>
        ) : (
          items.map((item) => {
            const isBusy = !!busy[item.id];
            const headline = item.title || item.name || item.slug || item.id;
            return (
              <article
                key={item.id}
                className="bg-white border border-border p-4 flex flex-col gap-3"
                data-testid={`submission-${item.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono px-2 py-0.5 bb-tag-neutral">PENDING</span>
                    <span className="text-[11px] font-mono text-bb-slate">by {item.submittedBy || "—"}</span>
                  </div>
                  <span className="text-[11px] font-mono text-bb-slate">Submitted {fmt(item.createdAt)}</span>
                </div>
                <h3 className="font-archivo text-base sm:text-lg text-bb-navy font-semibold leading-snug">
                  {headline}
                </h3>
                {renderBody(item)}
                {item.sourceUrl && (
                  <a
                    href={item.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-navy bb-link w-fit"
                  >
                    <ExternalLink size={11} /> Source
                  </a>
                )}
                <div className="flex items-center justify-end gap-2 pt-3 border-t border-border flex-wrap">
                  <button
                    onClick={() => act(item.id, "reject")}
                    disabled={isBusy}
                    data-testid={`submission-${item.id}-reject`}
                    className="inline-flex items-center gap-1 px-3 py-2.5 border border-border text-bb-slate text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]"
                  >
                    {isBusy ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />} Reject
                  </button>
                  <button
                    onClick={() => act(item.id, "approve")}
                    disabled={isBusy}
                    data-testid={`submission-${item.id}-approve`}
                    className="bb-btn-primary inline-flex items-center gap-1 px-3 py-2.5 text-sm disabled:opacity-50 min-h-[44px]"
                  >
                    {isBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Approve
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>

      <div className="bb-container py-6 text-center">
        <Link to="/" className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link">
          Back to site
        </Link>
      </div>
    </div>
  );
}
